/**
 * ProjectMessagesPanel — planTEK PM-client communication thread.
 *
 * Flat message list (V107: project_messages table) with:
 * - Internal-only badge for PM notes hidden from clients
 * - Compose area with Internal toggle (admin/user only)
 * - Delete own messages (or admin can delete any)
 * - Oldest-first ordering (natural conversation flow)
 */
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Trash2, Send, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  useProjectMessages,
  useSendProjectMessage,
  useDeleteProjectMessage,
} from '@/hooks/useProjectMessages';
import type { ProjectMessage } from '@/types/project_messages';

// ============================================================================
// Helpers
// ============================================================================

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) {
    return d.toLocaleDateString([], { weekday: 'short' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function initials(email: string): string {
  const [local] = email.split('@');
  const parts = local.split(/[._-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

function avatarColor(userId: string): string {
  const colors = [
    'bg-violet-500', 'bg-blue-500', 'bg-green-500', 'bg-orange-500',
    'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-amber-500',
  ];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) & 0xffffffff;
  }
  return colors[Math.abs(hash) % colors.length];
}

// ============================================================================
// Sub-components
// ============================================================================

interface MessageBubbleProps {
  message: ProjectMessage;
  currentUserId: string | undefined;
  isAdmin: boolean;
  onDelete: (msgId: string) => void;
  isDeleting: boolean;
}

function MessageBubble({ message, currentUserId, isAdmin, onDelete, isDeleting }: MessageBubbleProps) {
  const isOwn = message.sender_user_id === currentUserId;

  return (
    <div
      className={cn(
        'flex gap-3 group',
        isOwn ? 'flex-row-reverse' : 'flex-row',
        message.is_internal && 'opacity-90',
      )}
    >
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold',
          avatarColor(message.sender_user_id),
        )}
        title={message.sender_email}
      >
        {initials(message.sender_email)}
      </div>

      {/* Bubble */}
      <div className={cn('flex flex-col max-w-[75%]', isOwn && 'items-end')}>
        {/* Meta row */}
        <div className={cn('flex items-center gap-2 mb-1', isOwn && 'flex-row-reverse')}>
          <span className="text-xs text-gray-500 font-medium">
            {isOwn ? 'You' : message.sender_email.split('@')[0]}
          </span>
          {message.is_internal && (
            <Badge variant="outline" className="text-[10px] px-1 py-0 border-amber-400 text-amber-600 gap-0.5">
              <Lock className="w-2.5 h-2.5" />
              Internal
            </Badge>
          )}
          <span className="text-[10px] text-gray-400">{formatTime(message.created_at)}</span>
        </div>

        {/* Content */}
        <div
          className={cn(
            'px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words',
            isOwn
              ? 'bg-violet-600 text-white rounded-tr-sm'
              : 'bg-gray-100 text-gray-900 rounded-tl-sm',
            message.is_internal && !isOwn && 'bg-amber-50 border border-amber-200',
            message.is_internal && isOwn && 'bg-amber-500',
          )}
        >
          {message.content}
        </div>

        {/* Delete button (hover) */}
        {(isOwn || isAdmin) && (
          <button
            onClick={() => onDelete(message.id)}
            disabled={isDeleting}
            className="mt-1 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500 flex items-center gap-1 text-[10px]"
            title="Delete message"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

interface ProjectMessagesPanelProps {
  projectId: string;
}

export function ProjectMessagesPanel({ projectId }: ProjectMessagesPanelProps) {
  const { user } = useAuth();
  const currentUserId = user?.uid;
  const isAdmin = Boolean(user?.role === 'admin' || user?.role === 'super_admin');
  const isExternal = user?.role === 'external';

  const { data, isLoading } = useProjectMessages(projectId);
  const messages = data?.messages ?? [];

  const sendMessage = useSendProjectMessage();
  const deleteMessage = useDeleteProjectMessage();

  const [content, setContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async () => {
    const trimmed = content.trim();
    if (!trimmed) return;
    try {
      await sendMessage.mutateAsync({
        projectId,
        data: { content: trimmed, is_internal: isInternal },
      });
      setContent('');
    } catch {
      // toast is shown by the hook
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDelete = async (messageId: string) => {
    if (!confirm('Delete this message?')) return;
    await deleteMessage.mutateAsync({ projectId, messageId });
  };

  // ---- Loading skeleton ----
  if (isLoading) {
    return (
      <div className="space-y-4 p-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-3">
            <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-10 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Message thread */}
      <div className="flex flex-col gap-4 max-h-[480px] overflow-y-auto pr-1">
        {messages.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            No messages yet. Send the first update.
          </p>
        ) : (
          messages.map(msg => (
            <MessageBubble
              key={msg.id}
              message={msg}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              onDelete={handleDelete}
              isDeleting={deleteMessage.isPending}
            />
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {/* Compose area */}
      <div className="border-t border-gray-100 pt-4">
        <Textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write a message… (Ctrl+Enter to send)"
          className="min-h-[80px] resize-none text-sm"
          disabled={sendMessage.isPending}
        />
        <div className="flex items-center justify-between mt-2">
          {/* Internal toggle — hidden for external users */}
          {!isExternal && (
            <button
              type="button"
              onClick={() => setIsInternal(v => !v)}
              className={cn(
                'flex items-center gap-1.5 text-xs px-2 py-1 rounded-md transition-colors border',
                isInternal
                  ? 'bg-amber-50 border-amber-400 text-amber-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300',
              )}
            >
              <Lock className="w-3 h-3" />
              {isInternal ? 'Internal note (PM only)' : 'Internal note'}
            </button>
          )}
          {isExternal && <span />}

          <Button
            size="sm"
            onClick={handleSend}
            disabled={!content.trim() || sendMessage.isPending}
            className="gap-1.5"
          >
            <Send className="w-3.5 h-3.5" />
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
